/**
 * QuotaBar — a plan-usage bar for one metered metric (default: catalog items).
 *
 * Reads live usage from QuotaContext and renders the prepaid state matrix
 * (SKMETER-DB-001 §6):
 *   Healthy      < 80%   accent bar
 *   Approaching  80–99%  warn bar  + "recharge to avoid a block"
 *   At cap       ≥ 100%  danger bar + "new writes blocked — recharge or upgrade"
 *
 * Fail-open / calm: renders nothing when metering is off or usage isn't
 * reported, and a quiet "updating…" note when the meter is unreachable — never
 * an error. Theme- and responsive-aware via the shared utilities.
 */

import React from 'react';
import { View, Text } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useQuota } from '../context/quotaContext';
import { METRIC_CATALOG_ITEMS } from '../schemas/catalog/meter';

interface QuotaBarProps {
  /** Metric key to display. Defaults to the catalog item-count metric. */
  metric?: string;
  /** Heading shown above the bar. */
  label?: string;
}

function fmt(n: number): string {
  return Number(n).toLocaleString('en-IN');
}

export default function QuotaBar({
  metric = METRIC_CATALOG_ITEMS,
  label = 'Catalog items',
}: QuotaBarProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const { usage, byMetric } = useQuota();

  // Metering off for this deployment → show nothing (no clutter).
  if (!usage.enabled) return null;

  const m = byMetric(metric);

  const containerStyle = {
    backgroundColor: colors.card,
    borderColor: colors.line,
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  } as const;

  const labelStyle = {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.semibold,
    color: colors.ink2,
    fontFamily: font.sans,
  } as const;

  // Meter unreachable (degraded) or metric not reported yet → calm placeholder.
  if (!m || m.effective_cap == null) {
    if (!usage.degraded && m == null) return null; // enabled but nothing to show
    return (
      <View style={containerStyle}>
        <Text style={labelStyle}>{label}</Text>
        <Text style={{ marginTop: 6, fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>
          Usage updating…
        </Text>
      </View>
    );
  }

  const cap = m.effective_cap;
  const pct = cap > 0 ? Math.min(100, (m.used / cap) * 100) : 0;

  // Prepaid state matrix.
  const state: 'healthy' | 'approaching' | 'blocked' =
    m.blocked || m.used >= cap ? 'blocked' : pct >= 80 ? 'approaching' : 'healthy';

  const barColor =
    state === 'blocked' ? colors.danger : state === 'approaching' ? colors.warn : colors.accent;
  const trackColor = colors.line;

  const context =
    state === 'blocked'
      ? 'New items are blocked — recharge or upgrade to add capacity.'
      : state === 'approaching'
        ? 'Approaching your limit — recharge to avoid a block.'
        : null;
  const contextColor = state === 'blocked' ? colors.danger : colors.warn;

  // "used / cap" with base+blocks breakdown when prepaid blocks are active.
  const hasBlocks = (m.blocks_purchased_this_period ?? 0) > 0 && m.tier_cap != null;

  return (
    <View style={containerStyle}>
      <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <Text style={labelStyle}>{label}</Text>
        <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.mono }}>
          {fmt(m.used)} / {fmt(cap)}
        </Text>
      </View>

      {/* Progress track */}
      <View style={{ height: 8, borderRadius: 999, backgroundColor: trackColor, marginTop: 10, overflow: 'hidden' }}>
        <View style={{ height: 8, borderRadius: 999, width: `${pct}%`, backgroundColor: barColor }} />
      </View>

      {hasBlocks && (
        <Text style={{ marginTop: 6, fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
          base {fmt(m.tier_cap as number)} + {fmt((m.blocks_purchased_this_period as number))} block(s)
        </Text>
      )}

      {context && (
        <Text style={{ marginTop: 8, fontSize: fontSize.sm, color: contextColor, fontFamily: font.sans }}>
          {context}
        </Text>
      )}
    </View>
  );
}
